import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { LayoutService } from './services/app.layout.service';
import { AbstractCoreComponent } from '../shared/abstracts/abstract-core.component';

@Component({
  selector: '[app-menu-item]',
  templateUrl: './app.menu-item.component.html',
  animations: [
    trigger('children', [
      state(
        'collapsed',
        style({
          height: '0',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
        }),
      ),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
    ]),
  ],
  standalone: false,
})
export class AppMenuItemComponent extends AbstractCoreComponent implements OnInit {
  private readonly layoutService = inject(LayoutService);

  @Input() item!: MenuItem;

  @Input() index!: number;

  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

  @Input() parentKey!: string;

  active = false;

  key: string = '';

  constructor() {
    super();
    this.layoutService.menu$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (!value.routeEvent) {
          if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
            this.active = false;
          }
          return;
        }
        this.active = value.key === this.key || value.key.startsWith(this.key + '-');
      });
    });
    this.layoutService.reset$.subscribe(() => {
      this.active = false;
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
      if (this.item.routerLink) {
        this.updateActiveStateFromRoute();
      }
    })
  }

  ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);
    if (this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  updateActiveStateFromRoute() {
    const activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });
    if (activeRoute) {
      this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  itemClick(event: Event) {
    // avoid processing disabled items
    if (this.item.disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    // execute command
    if (this.item.command) {
      this.item.command({ originalEvent: event, item: this.item });
    }
    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
    }
    this.layoutService.onMenuStateChange({ key: this.key });
  }

  get auth() {
    try {
      return this.item['auth'];
    } catch (e) {
      return null;
    }
  }

  /**
   * Role constraint for this entry, or null when it has none.
   *
   * Null matters: `AuthDirective` treats a null option as "nothing to check" and
   * renders, so every existing entry keeps working untouched.
   */
  get roles() {
    try {
      return this.item['roles'] ?? null;
    } catch (e) {
      return null;
    }
  }

  get submenuAnimation() {
    return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root;
  }
}
